import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Title,
  Text,
  Badge,
  Group,
  Button,
  Table,
  Drawer,
  Stack,
  Tabs,
  LoadingOverlay,
  Center,
  Pagination,
  Modal,
  Divider,
} from "@mantine/core";
import { IconEye } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

const ADMIN_API = `${API_BASE_URL}/`;
const getAdminHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const TYPE_LABEL = { HUB: "Community", POST: "Post", COMMENT: "Comment", USER: "User" };

// The existing content-delete endpoints already used by the leader/member-
// facing app — this page reuses them rather than having its own "delete
// reported content" endpoint. Each already has a working admin bypass.
const contentDeleteUrl = (type, target) => {
  if (type === "HUB") return `${ADMIN_API}hubs/${target.hubId}`;
  if (type === "POST") return `${ADMIN_API}hubs/${target.hubId}/posts/${target.id}`;
  return `${ADMIN_API}hubs/${target.hubId}/posts/${target.postId}/comments/${target.id}`;
};

export default function UgcModerationReview() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("HUB");
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  const fetchReports = async (page = 1) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ type: activeTab, page, limit: 10 });
      const res = await fetch(`${ADMIN_API}hubs/admin/reports?${query}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        setReports(data.data.reports);
        setPagination({
          page: data.data.pagination.page,
          totalPages: data.data.pagination.totalPages,
        });
      } else {
        showError("Error", data.error?.message || "Failed to load reports.");
      }
    } catch (e) {
      showError("Error", "Network error while loading reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openReview = (report) => {
    setSelectedReport(report);
    setReviewOpen(true);
  };

  const closeAll = () => {
    setReviewOpen(false);
    setConfirmDeleteOpen(false);
    setSelectedReport(null);
  };

  const handleDismiss = async () => {
    if (!selectedReport) return;
    setActionLoading(true);
    try {
      const res = await fetch(
        `${ADMIN_API}hubs/admin/reports/${selectedReport.type}/${selectedReport.id}`,
        { method: "DELETE", headers: getAdminHeaders() },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess("Dismissed", "The report has been removed from the queue.");
        closeAll();
        fetchReports(pagination.page);
      } else {
        showError("Dismiss failed", data.error?.message || "Something went wrong.");
      }
    } catch (e) {
      showError("Error", "Network error while dismissing.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteContent = async () => {
    if (!selectedReport) return;
    setActionLoading(true);
    try {
      const res = await fetch(contentDeleteUrl(selectedReport.type, selectedReport.target), {
        method: "DELETE",
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess("Deleted", `The reported ${TYPE_LABEL[selectedReport.type].toLowerCase()} has been removed.`);
        closeAll();
        fetchReports(pagination.page);
      } else {
        showError("Delete failed", data.error?.message || "Something went wrong.");
      }
    } catch (e) {
      showError("Error", "Network error while deleting content.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box className="space-y-6">
      <Group justify="space-between">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            UGC Moderation
          </Text>
          <Text size="sm" c="dimmed">
            Review reported communities, posts, comments, and users. Dismiss clears a report from
            the queue; Delete Content soft-deletes the reported item itself, and View User opens
            the reported user's admin profile.
          </Text>
        </Box>
      </Group>

      <Paper withBorder radius="lg" shadow="sm" pos="relative">
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

        <Tabs value={activeTab} onChange={setActiveTab} color="blue" radius="md">
          <Tabs.List px="md" pt="md">
            <Tabs.Tab value="HUB">
              <Text size="md" fw={600}>Communities</Text>
            </Tabs.Tab>
            <Tabs.Tab value="POST">
              <Text size="md" fw={600}>Posts</Text>
            </Tabs.Tab>
            <Tabs.Tab value="COMMENT">
              <Text size="md" fw={600}>Comments</Text>
            </Tabs.Tab>
            <Tabs.Tab value="USER">
              <Text size="md" fw={600}>Users</Text>
            </Tabs.Tab>
          </Tabs.List>

          <Box p="md" mih={400}>
            <Table verticalSpacing="md" horizontalSpacing="md" highlightOnHover>
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Reporter</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Reason</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Target</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Community</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Submitted</Text></Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {reports.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6} ta="center" c="dimmed" py="xl">
                      <Text size="lg">No reported {TYPE_LABEL[activeTab].toLowerCase()}s.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  reports.map((report) => (
                    <Table.Tr key={report.id}>
                      <Table.Td>
                        <Text size="sm">{report.reporter?.nickname || "Unknown"}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1}>{report.reason}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" lineClamp={1}>{report.target.preview}</Text>
                      </Table.Td>
                      <Table.Td>
                        {report.type !== "USER" && (
                          <Badge color="gray" variant="light" size="sm">{report.target.hubName}</Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{new Date(report.createdAt).toLocaleDateString()}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          variant="light"
                          size="sm"
                          leftSection={<IconEye size={16} />}
                          onClick={() => openReview(report)}
                        >
                          Review
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Box>
        </Tabs>

        <Center p="md" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
          <Pagination
            total={pagination.totalPages}
            value={pagination.page}
            onChange={(p) => fetchReports(p)}
          />
        </Center>
      </Paper>

      {/* REVIEW DRAWER */}
      <Drawer
        opened={reviewOpen}
        onClose={closeAll}
        position="right"
        size="lg"
        title={<Title order={3} size="h2">Report Review</Title>}
        padding="xl"
        overlayProps={{ opacity: 0.5, blur: 4 }}
      >
        {selectedReport && (
          <Stack gap="xl">
            <Paper withBorder p="lg" radius="md" bg="gray.0">
              <Text size="sm" tt="uppercase" c="dimmed" fw={700} mb="sm">
                Reported {TYPE_LABEL[selectedReport.type]}
              </Text>
              <Text size="md">{selectedReport.target.preview}</Text>
              {selectedReport.type !== "USER" && (
                <Group gap="xs" mt="sm">
                  <Badge color="gray" variant="light">{selectedReport.target.hubName}</Badge>
                  {selectedReport.target.creator && (
                    <Badge color="blue" variant="dot">{selectedReport.target.creator.nickname}</Badge>
                  )}
                </Group>
              )}
            </Paper>

            <Box>
              <Title order={4} size="h3" mb="sm">Reason</Title>
              <Paper withBorder p="lg" radius="md">
                <Text size="sm">{selectedReport.reason}</Text>
              </Paper>
            </Box>

            <Box>
              <Title order={4} size="h3" mb="sm">Reported By</Title>
              <Paper withBorder p="lg" radius="md">
                <Text fw={600}>{selectedReport.reporter?.nickname || "Unknown"}</Text>
                <Text size="xs" c="dimmed">{new Date(selectedReport.createdAt).toLocaleString()}</Text>
              </Paper>
            </Box>

            <Box mt="auto" pt="xl" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
              <Group grow>
                <Button
                  size="lg"
                  variant="light"
                  loading={actionLoading}
                  onClick={handleDismiss}
                >
                  Dismiss
                </Button>
                {selectedReport.type === "USER" ? (
                  <Button
                    size="lg"
                    color="red"
                    variant="light"
                    disabled={actionLoading}
                    onClick={() => navigate(`/admin/dashboard/users/${selectedReport.target.id}`)}
                  >
                    View User
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    color="red"
                    disabled={actionLoading}
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    Delete Content
                  </Button>
                )}
              </Group>
            </Box>
          </Stack>
        )}
      </Drawer>

      {/* DELETE CONFIRMATION */}
      <Modal
        opened={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title={<Text fw={700}>Delete this {selectedReport && TYPE_LABEL[selectedReport.type].toLowerCase()}?</Text>}
      >
        <Stack>
          <Text size="sm" c="dimmed">
            This soft-deletes the reported content — it's removed from the app immediately. This
            action can't be undone from this screen.
          </Text>
          <Divider />
          <Group grow>
            <Button variant="default" onClick={() => setConfirmDeleteOpen(false)}>
              Cancel
            </Button>
            <Button color="red" loading={actionLoading} onClick={handleDeleteContent}>
              Confirm Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
